import type { CSSProperties } from "react";

/** HSL hues sampled from the body colour of each `/brand/agents/{name}.png` illustration. */
export const AGENT_PRESET_HUES: Readonly<Record<string, number>> = {
  blue: 198,
  pink: 329,
  red: 354,
  purple: 259,
  navy: 206,
  yellow: 62,
  orange: 17,
  green: 88,
};

/**
 * An agent's colour pair for solid surfaces (your message bubbles, the send button, the
 * computer stage). `fill` is the mascot's colour, darkened only as far as needed for `ink`
 * to pass WCAG AA (4.5:1) on it; the bright mascots (green, yellow) keep their fill and
 * take a dark ink instead of white.
 */
export interface AgentPalette {
  readonly fill: string;
  readonly ink: string;
}

const PRESET_PALETTES: Readonly<Record<string, AgentPalette>> = {
  blue: { fill: "#0977ae", ink: "#ffffff" },
  pink: { fill: "#da1b7e", ink: "#ffffff" },
  red: { fill: "#df2033", ink: "#ffffff" },
  purple: { fill: "#7849df", ink: "#ffffff" },
  navy: { fill: "#405f77", ink: "#ffffff" },
  orange: { fill: "#cb4510", ink: "#ffffff" },
  green: { fill: "#a3ec51", ink: "#20390e" },
  yellow: { fill: "#fafa2e", ink: "#413d10" },
};

/** Body colour of each preset character (sampled from the original artwork). */
const PRESET_BODIES: Readonly<Record<string, string>> = {
  blue: "#6fd0fb",
  pink: "#ff6db8",
  red: "#ff6676",
  purple: "#b08bfe",
  navy: "#546979",
  yellow: "#f9ff4c",
  orange: "#ff7f4c",
  green: "#a3f14c",
};

/** The body colour when `avatar` is one of the preset characters, else null. */
export function presetMascotBody(avatar: string): string | null {
  const color = presetColorFromAvatar(avatar);
  return color ? (PRESET_BODIES[color] ?? null) : null;
}

/** Preset mascot paths — same order as `MORROW_WORKSPACE_AGENTS` in morrowWorkspaceCatalog. */
export const AGENT_PRESET_MASCOT_PATHS: ReadonlyArray<string> = (
  Object.keys(AGENT_PRESET_HUES) as Array<keyof typeof AGENT_PRESET_HUES>
).map((name) => `/brand/agents/${name}.png`);

/** Stable hue from a string (FNV-1a-ish), used when no preset mascot colour applies. */
export function hueFrom(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % 360;
}

function presetColorFromAvatar(avatar: string): string | null {
  const match = avatar.trim().match(/\/agents\/([a-z]+)\.(?:png|jpe?g|webp|gif|svg)(?:\?.*)?$/i);
  return match ? match[1]!.toLowerCase() : null;
}

/** Hue for UI chrome: preset mascot colour when set, otherwise a stable hash of `seed`. */
export function agentHueFrom(avatar: string | null | undefined, seed: string): number {
  const color = avatar ? presetColorFromAvatar(avatar) : null;
  if (color && color in AGENT_PRESET_HUES) return AGENT_PRESET_HUES[color]!;
  return hueFrom(seed);
}

/** Colour pair for an agent: its preset mascot's palette, else one derived from its hue. */
export function agentPalette(avatar: string | null | undefined, seed: string): AgentPalette {
  const color = avatar ? presetColorFromAvatar(avatar) : null;
  if (color && PRESET_PALETTES[color]) return PRESET_PALETTES[color]!;
  return { fill: `hsl(${hueFrom(seed)} 62% 40%)`, ink: "#ffffff" };
}

export function mascotPickerSelectedStyle(avatar: string): CSSProperties {
  const ring = `hsl(${agentHueFrom(avatar, avatar)} 58% 46%)`;
  return {
    boxShadow: `0 0 0 2px var(--background), 0 0 0 4px ${ring}`,
  };
}

function resolveHue(seed: string, avatar?: string | null): number {
  return agentHueFrom(avatar, seed);
}

/** Semi-transparent wash derived from the agent — used for agent-side chat bubbles. */
export function agentTintBubbleStyle(seed: string, avatar?: string | null): CSSProperties {
  const hue = resolveHue(seed, avatar);
  return {
    background: `color-mix(in oklab, hsl(${hue} 58% 50%) 14%, transparent)`,
    borderColor: `color-mix(in oklab, hsl(${hue} 52% 46%) 26%, transparent)`,
  };
}

/** Subtle header / chrome tint for the agent chat pane. */
export function agentTintPaneStyle(seed: string, avatar?: string | null): CSSProperties {
  const hue = resolveHue(seed, avatar);
  return {
    background: `color-mix(in oklab, hsl(${hue} 48% 50%) 6%, var(--background))`,
  };
}

/**
 * Scoped chat tokens for an agent window — user bubbles (`bg-message`), send
 * button (`bg-message-action`), working rows (`bg-primary`), and focus rings.
 */
export function agentChatThemeStyle(seed: string, avatar?: string | null): CSSProperties {
  const hue = resolveHue(seed, avatar);
  const action = `hsl(${hue} 58% 46%)`;
  const actionHover = `hsl(${hue} 58% 40%)`;
  const actionFg = `hsl(0 0% 100%)`;
  const surface = `color-mix(in oklab, hsl(${hue} 56% 52%) 24%, var(--background))`;
  return {
    "--message-surface": surface,
    "--message-foreground": "var(--foreground)",
    "--message-action": action,
    "--message-action-foreground": actionFg,
    "--message-action-hover": actionHover,
    "--primary": action,
    "--primary-foreground": actionFg,
    "--ring": action,
  } as CSSProperties;
}
